/**
 * PropertyDetail
 * - parent component for several property detail view options
 * - select view using drop-down (xs) or tabs (sm & up)
 * - provide data as props to child components
 * - TODO: Calvin: Add functionality to allow deep-links to detail tabs using ? or #
 */

import React, { useEffect, useMemo, useState } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

import { useAuthenticator } from '../../auth/AuthenticationContext';
import { Notifier } from '../../system/services/notificationManager';
import { useInjectableComponents } from '../../system/services/injectableComponentsManager';
import { usePropertyPlmContext } from './PropertyContextWrapper';
import { usePropertyById } from '../../system/EntityManager';

import { Card, useTheme } from '@mui/material';
import { useIsSmallScreen } from '../../system/SystemMediaQueriesProvider';

import {
  PropertyQuery,
  Unit,
  usePropertyQuery,
  usePropertyUpdateSubscription,
} from '../../../types/generated-types';

import { DetailHeader } from './ui/detail-header';
import { DetailPanel } from './ui/detail-panel';
import FullWidthLoadingSkeleton from '../shared/fullWidthLoadingSkeleton';
import { viewOptionsMap, ViewOption } from './ui/property-detail-selector';
import { updateCacheFromSubscriptionEvent } from '../../../helpers/subscriptionUtils';

export type MyProperty = PropertyQuery['property'];

export function PropertyDetail() {
  const navigate = useNavigate();
  const { user } = useAuthenticator();
  const { pathname } = useLocation();

  // Call all hooks first
  const theme = useTheme();
  const isSmall = useIsSmallScreen();
  const { setTitle } = useInjectableComponents();

  // Get context normally - it should handle its own errors
  const { propertyId } = usePropertyPlmContext();
  const { property: managedProperty } = usePropertyById(propertyId);
  const pathViewKey = pathname.split('/')[3]?.replace('-', '').replace('/', '');
  const isSummaryRoute = pathViewKey === 'summary';
  const needsFullProperty = !isSummaryRoute;

  const { data, error } = usePropertyQuery({
    variables: { _id: propertyId },
    fetchPolicy: 'network-only',
    skip: !propertyId || !needsFullProperty,
  });
  const fullPropertyError = needsFullProperty ? error : undefined;
  const [selectedProperty, setSelectedProperty] = useState<MyProperty | null>(
    null,
  );
  const [selectedUnit, setSelectedUnit] = useState<Partial<Unit>>();
  const [selectedViewOption, setSelectedViewOption] = useState<ViewOption>(
    viewOptionsMap.Summary,
  );
  const routeSummaryProperty = useMemo(
    () =>
      isSummaryRoute && propertyId
        ? ({
            __typename: 'Property',
            _id: propertyId,
            title: managedProperty?.title,
          } as MyProperty)
        : null,
    [isSummaryRoute, managedProperty?.title, propertyId],
  );
  const propertyForRender =
    (needsFullProperty ? data?.property : undefined) ??
    selectedProperty ??
    routeSummaryProperty;

  useEffect(() => {
    if (propertyForRender?.title) {
      setTitle(propertyForRender.title);
    }
    return () => {
      setTitle(undefined);
    };
  }, [propertyForRender, setTitle]);

  useEffect(() => {
    if (fullPropertyError) {
      Notifier.error(
        'There was a problem fetching the property information: ',
        fullPropertyError,
      );
    }
  }, [fullPropertyError]);

  useEffect(() => {
    if (pathname && user) {
      const parts = pathname.split('/');
      const tab = parts[3]?.replace('-', '').replace('/', '');
      if (tab === undefined || tab === '') {
        setTimeout(() => {
          navigate(`./${user?.isInstaller ? 'installer' : 'summary'}`, {
            replace: true,
          });
        });
      } else {
        const foundOption = Object.values(viewOptionsMap).find(
          (option) => option.type.toLowerCase() === tab,
        );
        setSelectedViewOption(foundOption || viewOptionsMap.Summary);
      }
    }
  }, [pathname, user, navigate]);

  useEffect(() => {
    const thisProperty = needsFullProperty ? data?.property : undefined;
    if (thisProperty) {
      setSelectedProperty(thisProperty);
    }
  }, [data, needsFullProperty]);

  useEffect(() => {
    if (selectedProperty && pathname) {
      const pathParts = pathname.split('/');
      const unitId = pathParts[4];
      if (unitId) {
        const selectedUnit = selectedProperty.units.find(
          (aUnit) => aUnit?._id === unitId,
        );
        if (selectedUnit) {
          setSelectedUnit(selectedUnit);
        }
      } else {
        setSelectedUnit(undefined);
      }
    }
  }, [selectedProperty, pathname]);

  usePropertyUpdateSubscription({
    variables: { _id: propertyId },
    skip: !propertyId || !needsFullProperty,
    fetchPolicy: 'no-cache',
    onData: updateCacheFromSubscriptionEvent,
  });

  return fullPropertyError ? (
    <div>
      An error was encountered while loading property with id {propertyId}:{' '}
      {fullPropertyError.message}.
    </div>
  ) : !propertyForRender ? (
    <FullWidthLoadingSkeleton />
  ) : (
    <Card
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'top',
        flex: 1,
        alignItems: 'stretch',
        overflowY: 'scroll',
        background: 'inherit',
        margin: isSmall ? '0px' : '10px',
        '& .MuiCardHeader-root': {
          padding: '4px 10px !important',
          borderBottom: '1px solid darkgray',
          boxShadow: '0px 8px 6px -6px rgba(0, 0, 0, 0.35)',
        },
      }}
      elevation={4}
    >
      <DetailHeader property={propertyForRender} unit={selectedUnit} />
      <DetailPanel
        theme={theme}
        viewOption={selectedViewOption}
        data={propertyForRender}
      />
    </Card>
  );
}
